import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf } from "@/lib/format";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Crown, Check, Users } from "lucide-react";

export default function Settings() {
  const { wedding, user, refreshWedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const w = wedding?.wedding;
  const [form, setForm] = useState({ names: "", wedding_date: "", city: "", estimated_guests: 0, estimated_budget: 0 });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("COUPLE_PARTNER");
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (w) setForm({ names: w.names, wedding_date: w.wedding_date || "", city: w.city || "", estimated_guests: w.estimated_guests || 0, estimated_budget: w.estimated_budget || 0 });
    api.get("/plans").then(({ data }) => setPlans(data));
    if (wid) api.get(`/weddings/${wid}/subscription`).then(({ data }) => setSub(data));
  }, [w, wid]);

  const save = async () => { await api.patch(`/weddings/${wid}`, form); toast.success("Enregistré"); refreshWedding(); };
  const invite = async () => {
    try { const { data } = await api.post(`/weddings/${wid}/invite`, { email: inviteEmail, role: inviteRole });
      toast.success("Invitation envoyée"); setInviteOpen(false); setInviteEmail("");
      navigator.clipboard.writeText(`${window.location.origin}${data.link.startsWith("http") ? new URL(data.link).pathname : data.link}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const subscribe = async (code) => {
    // Paid plans ALWAYS go through Stripe Checkout. The frontend never activates Premium.
    try {
      const { data } = await api.post(`/payments/checkout`, {
        wedding_id: wid, plan_code: code, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Paiement indisponible pour le moment.");
    }
  };

  if (!w) return <Loader />;
  const currentPlan = sub?.plan?.code || "FREE";

  return (
    <div>
      <PageHeader eyebrow="Réglages" title="Paramètres du mariage" />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6 space-y-4">
          <h3 className="font-serif text-lg">Informations</h3>
          <div><Label>Noms</Label><Input value={form.names} onChange={(e) => setForm({ ...form, names: e.target.value })} className="mt-1 h-10" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.wedding_date} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} className="mt-1 h-10" /></div>
            <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-10" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Invités estimés</Label><Input type="number" value={form.estimated_guests} onChange={(e) => setForm({ ...form, estimated_guests: +e.target.value })} className="mt-1 h-10" /></div>
            <div><Label>Budget (CHF)</Label><Input type="number" value={form.estimated_budget} onChange={(e) => setForm({ ...form, estimated_budget: +e.target.value })} className="mt-1 h-10" /></div>
          </div>
          <Button onClick={save} data-testid="save-settings" className="rounded-full">Enregistrer</Button>
        </div>

        <div className="card-lux p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg">Espace partagé</h3>
            <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)} data-testid="invite-btn"><UserPlus className="w-4 h-4 mr-1" /> Inviter</Button>
          </div>
          <div className="space-y-2">
            {(wedding.members || []).map((m) => (
              <div key={m.user_id} className="flex items-center gap-3 p-3 rounded-xl border">
                <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-sm font-medium">{(m.name || "?")[0]}</div>
                <div className="flex-1"><div className="text-sm font-medium">{m.name || m.email}</div>
                  <div className="text-xs text-stone-400">{m.email}</div></div>
                <span className="text-xs px-2 py-1 rounded-full bg-accent flex items-center gap-1">
                  {m.role === "COUPLE_OWNER" && <Crown className="w-3 h-3 text-champagne" />}
                  {m.role === "COUPLE_OWNER" ? "Propriétaire" : m.role === "COUPLE_PARTNER" ? "Partenaire" : "Collaborateur"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-lux p-6 lg:col-span-2">
          <h3 className="font-serif text-lg mb-1">Abonnement</h3>
          <p className="text-sm text-stone-500 mb-4">Forfait actuel : <b>{sub?.plan?.name || "Gratuit"}</b>. Vos données sont toujours conservées, même après expiration.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((p) => (
              <div key={p.code} className={`rounded-xl border p-4 ${currentPlan === p.code ? "ring-2 ring-sage" : ""}`}>
                <div className="font-serif text-lg">{p.name}</div>
                <div className="font-mono text-xl mt-1">{p.amount === 0 ? "Gratuit" : chf(p.amount)}</div>
                <div className="text-xs text-stone-400 mb-3">{p.billing_type === "monthly" ? "par mois" : p.billing_type === "yearly" ? "par an" : p.billing_type === "one_time" ? "paiement unique" : ""}</div>
                {currentPlan === p.code ? (
                  <div className="text-sage text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Actif</div>
                ) : (
                  <Button size="sm" onClick={() => subscribe(p.code)} data-testid={`subscribe-${p.code}`} className="w-full rounded-full">
                    {p.amount === 0 ? "Revenir" : "Choisir"}</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent><DialogHeader><DialogTitle>Inviter dans votre espace</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>E-mail</Label><Input data-testid="invite-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="mt-1 h-10" /></div>
            <div><Label>Rôle</Label>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="mt-1 h-10 w-full rounded-md border px-2">
                <option value="COUPLE_PARTNER">Partenaire (accès complet)</option>
                <option value="COLLABORATOR">Collaborateur (accès limité)</option></select></div>
          </div>
          <DialogFooter><Button data-testid="send-invite" onClick={invite} className="rounded-full">Envoyer l'invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
